import { useRef, useState, useEffect } from "react";
import {
  Route,
  Layers,
  WifiOff,
  Languages,
  ShieldAlert,
  Sparkles,
  Plug,
  MapPinned,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// Palette for this section — kept in one place so the "deep green + Sunshine"
// intent stays obvious to anyone touching it.
const FOREST_GREEN = "#19532B";     // panel background — deeper than sections above
const SUNSHINE = "#FFCA26";         // eyebrow + icon + arrow-button accent

const CARDS = [
  { icon: Route, title: "Dispatch & routing", lines: ["Offer cascades, timeouts and fallback assignment,", "built for real traffic and patchy signal."] },
  { icon: Layers, title: "Multi-tenant architecture", lines: ["Tenant isolation, metering and billing", "designed before the first customer signs."] },
  { icon: WifiOff, title: "Offline-first mobile", lines: ["Apps that keep working when the driver loses signal,", "and sync when it returns."] },
  { icon: Languages, title: "Bilingual & RTL", lines: ["Arabic designed as a first language,", "not a mirrored layout."] },
  { icon: ShieldAlert, title: "Risk & control modelling", lines: ["Inherent and residual scoring,", "framework mapping, evidence chains."] },
  { icon: Sparkles, title: "AI in production", lines: ["Cost ceilings, evaluation loops and human", "approval gates, not demos."] },
  { icon: Plug, title: "Systems integration", lines: ["Making the systems you already own", "stop being islands."] },
  { icon: MapPinned, title: "Data residency design", lines: ["Mapping where every component sits,", "and what that means legally."] },
];

/**
 * Arrow button — outline in Sunshine at 50%. On hover full opacity + filled
 * arrow. Disabled state at 20%. No violet anywhere.
 */
function ArrowButton({ direction, onClick, disabled, testid }) {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === "prev" ? "Previous" : "Next"}
      data-testid={testid}
      style={{
        borderColor: SUNSHINE,
        color: SUNSHINE,
        opacity: disabled ? 0.2 : 0.5,
      }}
      className="group inline-flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-200 enabled:hover:!opacity-100 enabled:hover:bg-[color:#FFCA26]/10"
    >
      <Icon
        className="h-4 w-4 transition-colors"
      />
    </button>
  );
}

export function ExpertiseBand() {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const [progress, setProgress] = useState(0);

  const update = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    const max = el.scrollWidth - el.clientWidth;
    setProgress(max > 0 ? Math.min(1, Math.max(0, el.scrollLeft / max)) : 0);
  };

  useEffect(() => {
    update();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const scrollBy = (delta) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  return (
    <section
      data-testid="expertise-band"
      className="relative overflow-hidden text-white grain-overlay"
      style={{ backgroundColor: FOREST_GREEN }}
    >
      <div className="mx-auto max-w-7xl px-4 py-20 lg:px-6 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-3xl">
            <div
              className="text-xs font-semibold uppercase tracking-[0.22em]"
              style={{ color: SUNSHINE }}
            >
              Engineering depth
            </div>
            <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Where our engineering depth actually sits.
            </h2>
            <p className="mt-5 max-w-2xl text-sm text-white/75 lg:text-base">
              Eight areas we&apos;ve had to solve in production — not in a slide, not in a proof of
              concept. Drag or use the arrows to see them all.
            </p>
          </div>

          <div className="hidden items-center gap-2 md:flex">
            <ArrowButton direction="prev" onClick={() => scrollBy(-360)} disabled={!canLeft} testid="expertise-prev" />
            <ArrowButton direction="next" onClick={() => scrollBy(360)} disabled={!canRight} testid="expertise-next" />
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="wafa-snap-row mt-12 flex gap-5 overflow-x-auto pb-4"
          data-testid="expertise-scroller"
        >
          {CARDS.map(({ icon: Icon, title, lines }, i) => (
            <article
              key={title}
              data-testid={`expertise-card-${i}`}
              className="wafa-snap-item group flex w-[280px] shrink-0 flex-col rounded-lg border p-6 backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5 sm:w-[320px]"
              style={{
                backgroundColor: "rgba(255,255,255,0.06)",
                borderColor: "rgba(255,255,255,0.12)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,202,38,0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
              }}
            >
              <div
                className="flex h-11 w-11 items-center justify-center rounded-md"
                style={{ backgroundColor: "rgba(255,202,38,0.12)", color: SUNSHINE }}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-6 font-heading text-lg font-semibold text-white">{title}</div>
              <div className="mt-3 text-sm leading-relaxed text-white/70">
                {lines.join(" ")}
              </div>
            </article>
          ))}
        </div>

        {/* Scroll progress indicator — Sunshine on a low-opacity white track. */}
        <div
          className="mt-6 hidden h-[3px] w-full max-w-md rounded-full md:block"
          style={{ backgroundColor: "rgba(255,255,255,0.12)" }}
          aria-hidden="true"
        >
          <div
            data-testid="expertise-progress"
            className="h-full rounded-full transition-[width] duration-200"
            style={{ width: `${Math.round(progress * 100)}%`, backgroundColor: SUNSHINE }}
          />
        </div>
      </div>
    </section>
  );
}
